/**
 * VersionBadge：顶栏版本徽章（LeapWorldFlow-14）—— World Version 的用户可见面
 * （`docs/architecture/19-concepts.md` §4「World Version」词条：面向用户语言的上层，
 * 别和 git tag / GIT_SHA / baseVersion 本身混——那些是它读的底层基质，不是它自己）。
 *
 * 前端版本读构建期注入的 `import.meta.env.VITE_APP_VERSION` / `VITE_GIT_SHA`
 * （各 app Dockerfile 的 build 阶段设置，`vite build` 自动把 `VITE_` 前缀 env 塞进
 * `import.meta.env`）；后端版本 fetch `/api/health` 取 `appVersion` / `version`(sha) /
 * `baseVersion`（`@bf/logger` buildHealthPayload）。
 *
 * 前后端 git sha 不一致 → 告警色 + 提示「前端可能是旧的：刷新页面；若仍不一致，
 * 可能只部署了一端」，用来抓「浏览器开着旧前端标签 / 跑着旧版忘了部署」（单镜像部署下
 * 前后端 sha 天然一致，见工单已核实事实）。本地开发 / 未注入 GIT_SHA 时两端都会读到
 * "dev"，一律降级显示、不参与不一致判定、不误告警。
 *
 * 评审修正历史（新读者只需看这节，不必翻 git blame）：
 * - **M3**：无论 HTTP 状态码都解析响应体（503 时 body 里仍可能有真实版本字段，不能因
 *   `!response.ok` 就整体丢弃）；"取不到"（网络失败/响应体解析失败）与"真 dev"（后端
 *   明确返回、GIT_SHA 确实未注入）分开显示，都不参与一致性判定。
 * - **M4**：点开弹层时重新 fetch 一次，长开的标签页也能看到后端最新状态（挂载时仍保留
 *   一次 fetch，用于常态徽章的告警色判断，不用等用户点开才知道要不要告警）。
 * - **R1（StrictMode 回归）**：`mountedRef` 曾经只有 cleanup 置 false、setup 不复位——
 *   React 18 StrictMode 开发态的 mount→cleanup→remount 序列会把它永久卡在 false，
 *   两次 fetch 的结果都被当成「组件已卸载」而丢弃，徽章永久停在挂载前的初始态、
 *   不会告警。修法：effect 的 setup 里也要把它置回 true。
 * - **R2（弹层出屏）**：`align` 区分两种挂载语境——移动顶栏（'topbar'，视口顶部，弹层
 *   向下展开）与桌面侧栏/抽屉用户区（'sidebar'，贴视口底部，弹层若还向下展开会被推出
 *   视口——评审实测 1280×800 下弹层 68% 在屏外、bottom 超出 vh），后者改为向上、左对齐
 *   展开。
 * - **R4（M3 只堵一半）**：非 2xx 状态码不再直接判定为"取不到"，但如果响应体是一段
 *   语法合法却根本不是 health 形状的 JSON（网关错误页 `{"error": "..."}` 之类），
 *   仍会被 `normalize()` 的字段级降级悄悄读成 "dev"——加一道"像不像 health payload"
 *   的形状校验，不像就一并归入"取不到"。
 * - **R5（副作用写在 setState updater 里）**：点开弹层时的 `fetchHealth()` 曾经写在
 *   `setOpen` 的函数式 updater 内部——updater 必须是纯函数，StrictMode 会双跑它来检测
 *   这类问题，实测点开一次真的打了两次 `/api/health`。改成在事件处理函数体内直接调用。
 */
import { useEffect, useRef, useState } from 'react'
import { Badge } from './Badge'

const DEV = 'dev'
const UNKNOWN = '未知'
const UNVERSIONED = 'unversioned'

export type VersionBadgeAlign = 'topbar' | 'sidebar'

type HealthVersionFields = {
  version?: string
  appVersion?: string
  baseVersion?: string
}

/** 后端读取结果：null=尚未取到（挂载后的首次请求还没回来）；'unreachable'=网络失败、响应体
 *  解析失败，或响应体语法合法但形状不像 health payload（R4）；否则是解析出来的字段
 *  （哪怕来自非 2xx 响应，M3）。 */
type BackendState = HealthVersionFields | 'unreachable' | null

function normalize(value: string | undefined | null, fallback: string): string {
  const trimmed = value?.trim()
  return trimmed ? trimmed : fallback
}

function shortSha(sha: string): string {
  return sha === DEV || sha === UNKNOWN ? sha : sha.slice(0, 7)
}

/** 只有两端都读到真实 sha（非 "dev"/"未知"）才参与一致性判定，避免本地开发或取不到时误告警。 */
function isComparableSha(sha: string): boolean {
  return sha !== DEV && sha !== UNKNOWN && sha.length > 0
}

/**
 * 形状校验（R4）：网关/反代错误页常见 `{"error": "Bad Gateway"}` 这类合法 JSON，但压根不是
 * health 响应——只看字段是否存在（不是空字符串就行），存在任一 `buildHealthPayload` 必有的
 * 伴生字段（`service`/`ok`）或本组件关心的 `version` 字段，才当作"取到了"，否则一律归"取不到"，
 * 走 UNKNOWN 展示，不能落进 `normalize()` 的逐字段降级被误读成 "dev"。
 */
function looksLikeHealthPayload(data: unknown): data is HealthVersionFields {
  if (!data || typeof data !== 'object') return false
  const record = data as Record<string, unknown>
  return 'version' in record || 'service' in record || 'ok' in record
}

function backendField(backend: BackendState, key: keyof HealthVersionFields, fallback: string): string {
  if (backend === null || backend === 'unreachable') return UNKNOWN
  return normalize(backend[key], fallback)
}

/**
 * World Version 展开摘要（LeapWorldTwin-122）：`GET /api/world-version` 返回完全体 `WorldVersion`
 * （`@bf/contracts` 的 `world-version.ts`），但本组件只展示「版本叙事三条 + 定制点数 + 上次升级」
 * 三样——**不在这里 import 整份契约类型**，只声明用得到的最小字段（与 `HealthVersionFields`
 * 同一取舍：`packages/ui` 不为一个展示组件新增跨包依赖，08-packages.md §2.1 四问②）。
 */
type WorldVersionSummaryFields = {
  instance?: { app?: string; customer?: string; deployTarget?: string }
  base?: { version?: string }
  app?: { version?: string }
  narrative?: Array<{ version?: unknown; date?: unknown; oneLiner?: unknown }>
  customizations?: Array<{ path?: string; level?: string; note?: string }>
  lastUpgrade?: { from?: unknown; to?: unknown; at?: unknown } | null
}

type WorldVersionState = WorldVersionSummaryFields | 'unreachable' | null

/** 形状校验同 R4 思路：只要能看出「像」一份 World Version（narrative 数组存在）就当作取到了，
 * 网关错误页 / 未部署导出物（模板本仓内直跑会走 routes/world-version.ts 的降级占位，
 * 那也是合法形状）都不会被这里挡住；语法合法但压根不是这个形状的响应才归"取不到"。 */
function looksLikeWorldVersionPayload(data: unknown): data is WorldVersionSummaryFields {
  if (!data || typeof data !== 'object') return false
  const record = data as Record<string, unknown>
  return Array.isArray(record.narrative)
}

function narrativeLines(worldVersion: WorldVersionState): Array<{ version: string; date: string; oneLiner: string }> {
  if (worldVersion === null || worldVersion === 'unreachable' || !worldVersion.narrative) return []
  return worldVersion.narrative
    .map((entry) => ({
      version: typeof entry.version === 'string' ? entry.version : UNKNOWN,
      date: typeof entry.date === 'string' ? entry.date : '',
      oneLiner: typeof entry.oneLiner === 'string' ? entry.oneLiner : ''
    }))
    .filter((entry) => entry.oneLiner.length > 0)
}

/** 上次升级摘要：没有真实升级（null）时显示「尚未升级」，不伪造一条。 */
function lastUpgradeLine(worldVersion: WorldVersionState): string {
  if (worldVersion === null || worldVersion === 'unreachable') return UNKNOWN
  const upgrade = worldVersion.lastUpgrade
  if (!upgrade || typeof upgrade.from !== 'string' || typeof upgrade.to !== 'string') return '尚未升级'
  const at = typeof upgrade.at === 'string' && upgrade.at ? `（${upgrade.at}）` : ''
  return `${upgrade.from.slice(0, 7)} → ${upgrade.to.slice(0, 7)}${at}`
}

/** "v未知 · 未知" 读起来像格式错误；取不到时整行就显示"未知"一个词（评审「顺手改通顺」）。 */
function formatVersionLine(version: string, sha: string): string {
  if (sha === UNKNOWN) return UNKNOWN
  return `v${version} · ${sha}`
}

export function VersionBadge({ align = 'topbar' }: { align?: VersionBadgeAlign } = {}) {
  const [open, setOpen] = useState(false)
  const [backend, setBackend] = useState<BackendState>(null)
  const [worldVersion, setWorldVersion] = useState<WorldVersionState>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  const mountedRef = useRef(false)

  // R1：setup 也要把 mountedRef 置 true，不能只在 cleanup 里置 false——StrictMode 开发态的
  // mount→cleanup→remount 序列会让"只有 cleanup"的写法永久卡在 false，两次 fetch 的结果都会
  // 被当成"组件已卸载"丢弃，徽章因此永远停在初始态、真实的前后端不一致也不会告警。
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const frontendSha = normalize(import.meta.env.VITE_GIT_SHA, DEV)
  const frontendVersion = normalize(import.meta.env.VITE_APP_VERSION, DEV)

  // 不用 useCallback：本函数只在 effect 与点击里各调一次，没有依赖需要稳定引用，
  // 每次渲染重建也无所谓（不进任何依赖数组）。
  function fetchHealth() {
    fetch('/api/health')
      .then((response) => response.json()) // 状态码不参与判断（M3）：503 时 body 里仍可能有真实版本字段
      .then((data: unknown) => {
        if (!mountedRef.current) return
        setBackend(looksLikeHealthPayload(data) ? data : 'unreachable') // R4：形状不对也归"取不到"
      })
      .catch(() => {
        if (mountedRef.current) setBackend('unreachable')
      })
  }

  useEffect(() => {
    fetchHealth()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 仅挂载时取一次，弹层点开时另有 fetchHealth() 调用（M4）
  }, [])

  // World Version 摘要（LeapWorldTwin-122）：只在弹层点开时取——折叠态徽章不需要它，
  // 不为一份「点开才看」的数据在每次挂载都多打一次请求。
  function fetchWorldVersion() {
    fetch('/api/world-version')
      .then((response) => response.json())
      .then((data: unknown) => {
        if (!mountedRef.current) return
        setWorldVersion(looksLikeWorldVersionPayload(data) ? data : 'unreachable')
      })
      .catch(() => {
        if (mountedRef.current) setWorldVersion('unreachable')
      })
  }

  const backendSha = backendField(backend, 'version', DEV)
  const backendVersion = backendField(backend, 'appVersion', DEV)
  const backendBaseVersion = backend !== null && backend !== 'unreachable' ? normalize(backend.baseVersion, UNVERSIONED) : UNKNOWN

  const mismatch = isComparableSha(frontendSha) && isComparableSha(backendSha) && frontendSha !== backendSha

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  // R5：副作用（fetchHealth）不能写在 setOpen 的函数式 updater 里——updater 必须是纯函数，
  // StrictMode 会双跑它来检测这类问题，写在里面会导致点开一次实际打两次请求。这里直接在
  // 事件处理函数体内读当前 `open`（渲染闭包里的值，点击时足够新鲜，不需要函数式写法）。
  const toggleOpen = () => {
    const opening = !open
    if (opening) {
      fetchHealth() // 点开即重取（M4）：长开的标签页也能看到后端最新版本
      fetchWorldVersion()
    }
    setOpen(opening)
  }

  const popoverPositionClass = align === 'sidebar' ? 'absolute bottom-full left-0 mb-1' : 'absolute right-0 top-full mt-1'
  const worldVersionNarrative = narrativeLines(worldVersion)
  const worldVersionLastUpgrade = lastUpgradeLine(worldVersion)

  return (
    <div ref={rootRef} className="relative inline-flex shrink-0">
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={mismatch ? '版本信息：前后端版本不一致，点击查看详情' : '版本信息，点击查看详情'}
        className="inline-flex cursor-pointer items-center justify-center rounded-[var(--radius-pill)] focus:outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--color-focus)] max-md:min-h-11 max-md:min-w-11"
        onClick={toggleOpen}
      >
        <Badge tone={mismatch ? 'warning' : 'neutral'} dot={mismatch}>
          v{frontendVersion} · {shortSha(frontendSha)}
        </Badge>
      </button>
      {open ? (
        <div
          role="dialog"
          aria-label="版本详情"
          className={`${popoverPositionClass} z-50 w-80 max-w-[calc(100vw-1.5rem)] max-h-[75dvh] overflow-y-auto break-words rounded-[var(--radius-md)] border border-[var(--color-line)] bg-[var(--color-panel)] p-3 text-xs text-[var(--color-ink)] shadow-[var(--shadow-md)]`}
        >
          {mismatch ? (
            <p className="m-0 mb-2 rounded-[var(--radius-sm)] bg-[var(--color-warning-soft)] px-2 py-1.5 leading-4 text-[var(--color-warning-ink)]">
              前端可能是旧的：刷新页面；若仍不一致，可能只部署了一端。
            </p>
          ) : null}
          <h3 className="m-0 mb-2 text-sm font-semibold">我装的是什么</h3>
          {worldVersion && worldVersion !== 'unreachable' ? (
            <dl className="m-0 mb-2 grid grid-cols-[auto_minmax(0,1fr)] gap-x-2 gap-y-1">
              <dt className="text-[var(--color-faint)]">应用</dt><dd className="m-0">{worldVersion.instance?.app || UNKNOWN} · {worldVersion.app?.version || backendVersion}</dd>
              <dt className="text-[var(--color-faint)]">标准版</dt><dd className="m-0">{worldVersion.base?.version || backendBaseVersion}</dd>
              <dt className="text-[var(--color-faint)]">部署标识</dt><dd className="m-0">{worldVersion.instance?.deployTarget || '未设置'}</dd>
              {worldVersion.instance?.customer ? <><dt className="text-[var(--color-faint)]">客户</dt><dd className="m-0">{worldVersion.instance.customer}</dd></> : null}
            </dl>
          ) : <p>{worldVersion === null ? '正在读取版本信息…' : '版本信息暂时无法读取，请关闭后重试。'}</p>}
          <details>
          <summary className="flex min-h-11 cursor-pointer items-center text-[var(--color-faint)]">技术版本信息</summary>
          <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-2 gap-y-1">
            <dt className="text-[var(--color-faint)]">前端</dt>
            <dd className="m-0 truncate text-[var(--color-ink)]" title={formatVersionLine(frontendVersion, frontendSha)}>
              {formatVersionLine(frontendVersion, frontendSha)}
            </dd>
            <dt className="text-[var(--color-faint)]">后端</dt>
            <dd className="m-0 truncate text-[var(--color-ink)]" title={formatVersionLine(backendVersion, backendSha)}>
              {formatVersionLine(backendVersion, backendSha)}
            </dd>
            <dt className="text-[var(--color-faint)]">底座</dt>
            <dd className="m-0 truncate text-[var(--color-ink)]">{backendBaseVersion}</dd>
          </dl>
          </details>
          {/* World Version 摘要（LeapWorldTwin-122）：版本叙事三条 + 定制点数 + 上次升级——
              取不到（'unreachable'）或还没取到（null）时整块不渲染，不占位显示半份信息。 */}
          {worldVersion !== null && worldVersion !== 'unreachable' ? (
            <div className="mt-2 border-t border-[var(--color-line)] pt-2">
              <h3 className="m-0 mb-2 text-sm font-semibold">与标准版差在哪</h3>
              {worldVersion.customizations?.length ? (
                <ul className="m-0 mb-3 list-none space-y-2 p-0">
                  {worldVersion.customizations.map((entry, index) => (
                    <li key={`${entry.path}-${index}`}>
                      <p className="m-0 font-medium break-all">{entry.path || '未标注路径'}</p>
                      <p className="m-0 text-[var(--color-faint)]">{entry.level || '未标注类型'}</p>
                      <p className="m-0">{entry.note || '未填写摘要'}</p>
                    </li>
                  ))}
                </ul>
              ) : <p className="m-0 mb-3 text-[var(--color-faint)]">无本地定制</p>}
              <h3 className="m-0 mb-2 border-t border-[var(--color-line)] pt-2 text-sm font-semibold">这次升了什么</h3>
              <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-2 gap-y-1">
                <dt className="text-[var(--color-faint)]">上次升级</dt>
                <dd className="m-0 break-all text-[var(--color-ink)]">{worldVersionLastUpgrade}</dd>
              </dl>
              {worldVersionNarrative.length > 0 ? (
                <details className="mt-1.5" open>
                  <summary className="flex min-h-11 cursor-pointer items-center text-[var(--color-faint)]">上线内容（{worldVersionNarrative.length} 条）</summary>
                  <ul className="m-0 list-none space-y-1 p-0">
                    {worldVersionNarrative.map((entry) => (
                      <li key={`${entry.version}-${entry.date}`} className="text-[var(--color-ink)]">
                        <span className="text-[var(--color-faint)]">{entry.date} · {entry.version}</span>
                        <br />
                        <span>{entry.oneLiner}</span>
                      </li>
                    ))}
                  </ul>
                </details>
              ) : <p className="text-[var(--color-faint)]">尚无已公布的上线内容</p>}
            </div>
          ) : null}
          <a href="/whats-new" className="mt-2 flex min-h-11 items-center border-t border-[var(--color-line)] pt-2 font-medium text-[var(--color-ink)] underline underline-offset-4">查看全部上线内容</a>
        </div>
      ) : null}
    </div>
  )
}
